import type { TextPromptWithInputItem } from '@/app/types';
import type { ChatRequestContent } from '../_hook/use-chat-stream/types';

export interface PendingAttachmentReplacement {
  tempFilePath: string;
  filePath: string;
}

export const stringifyTextPromptWithPendingAttachmentReplacements = (
  value: TextPromptWithInputItem,
  replacements: PendingAttachmentReplacement[],
): string => {
  const replacementByTempPath = new Map(
    replacements
      .filter((replacement) => replacement.tempFilePath && replacement.filePath)
      .map((replacement) => [replacement.tempFilePath, replacement.filePath]),
  );

  return value
    .map((item) => {
      if (item.type === 'text') return item.text;
      if (item.type === 'mention') {
        const mentionId = item.mention.id || item.mention.name;
        return `@${replacementByTempPath.get(mentionId) ?? mentionId}`;
      }
      return '';
    })
    .join('')
    .trim();
};

/**
 * 把编辑器 inputValue 序列化成后端 `contents[]`：
 * - text：文本段 / mention(@真实路径) / skill(label) / upload(@真实路径) / input·select(用户填值) 内联拼接；
 * - skill：每个 skill token 的 payload(value) hoist 成独立 `{ type: 'skill' }`；
 * 媒体（image/video/...）不在这里 hoist —— upload 槽内的文件已经通过 attachments 进了 allFiles，
 * 由调用方单独 map，避免重复。temp→真实路径替换与 mention 共用 replacements（key=tempFilePath）。
 */
export const buildClawMessageContentsFromInputValue = (
  value: TextPromptWithInputItem,
  replacements: PendingAttachmentReplacement[] = [],
): { text: string; skillContents: ChatRequestContent[] } => {
  const replacementByTempPath = new Map(
    replacements
      .filter((replacement) => replacement.tempFilePath && replacement.filePath)
      .map((replacement) => [replacement.tempFilePath, replacement.filePath]),
  );
  const resolvePath = (path: string) => replacementByTempPath.get(path) ?? path;

  let text = '';
  const skillContents: ChatRequestContent[] = [];

  for (const item of value) {
    if (item.type === 'text') {
      text += item.text;
    } else if (item.type === 'mention') {
      const mentionId = item.mention.id || item.mention.name;
      text += `@${resolvePath(mentionId)}`;
    } else if (item.type === 'claw-template-token') {
      const token = item.clawTemplateToken;
      if (token.kind === 'skill') {
        text += token.label;
        // installed skill 必须显式带 source，否则后端当平台 skill 去 OSS 找不到；
        // platform / 缺省（如 quick-prompt 场景卡）不带，后端按 platform 处理。
        if (token.value) {
          const source = token.source === 'installed' ? 'installed' : 'platform';
          skillContents.push({ type: 'skill', content: token.value, source });
        }
      } else if (token.kind === 'upload') {
        for (const file of token.files ?? []) {
          text += `@${resolvePath(file.filePath)}`;
        }
      } else {
        text += token.value?.trim() ?? '';
      }
    }
  }

  return { text: text.trim(), skillContents };
};
